import { Block } from "../../../domain/blocks/block";
import { IBlockRepository } from "../../../domain/blocks/i-block.repository";
import moment from "moment";
import { concatMap, startWith, Subject, Subscription } from "rxjs";
import { ShowRepository } from "../../../domain/show-repository";
import { CalendarService } from "../../../domain/calendar-service";
import { GraphicsShow, MediaShow, Show } from "../../../domain/show";
import { Router } from "@angular/router";
import { UI } from "../../../services/ui.service";
import { NgxSmartModalService } from "ngx-smart-modal";
import { ChangeDetectorRef } from "@angular/core";
import { CalendarListComponent } from "../../calendar/calendar-list.component";
import { debounceTime } from "rxjs/operators";

export class EditBlockViewModel extends Block {
  showsChangedSubscription: Subscription;
  private _duringArrangeShows: boolean = false;
  public blockUpdated: Subject<void> = new Subject<void>();
  public loadingBlock: boolean = false;
  constructor(
    private _block: Block,
    private _blockRepository: IBlockRepository,
    private _showsRepository: ShowRepository,
    private _calendarService: CalendarService,
    private _router: Router,
    private _ui: UI,
    private _ngxSmartModalService: NgxSmartModalService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _calendarListComponent: CalendarListComponent,
  ) {
    super(
      _block.id,
      _block.uid,
      _block.description,
      _block.name,
      _block.packshotUrl,
      _block.showsCount,
      _block.duration,
      _block.isBlockStack,
    );
  }

  public async onInit() {
    console.log(
      "EditBlockViewModel->subscribing onShowsChange",
      this._calendarListComponent,
    );
    try {
      this.loadingBlock = true;
      this._calendarService.clearCache(true);

      if (this._calendarListComponent) {
        this._calendarListComponent.isLoading = true;
        this._calendarListComponent.ngOnInit();
        this._calendarListComponent.showsChangedSubscription?.unsubscribe();
      }
      this.showsChangedSubscription = this._showsRepository.onShowsChange
        .pipe(debounceTime(800))
        .subscribe(this.onShowsChangeAction.bind(this));

      /*this._calendarListComponent.showsChangedSubscription = this._showsRepository.onShowsChange.subscribe(async () => {
        await this._calendarListComponent.refreshView();
      });*/

      await this._showsRepository.arrangeShows(
        this.id,
        moment().startOf("day"),
        moment().startOf("day").add(24, "hours"),
        true,
      );
    } finally {
      this.loadingBlock = false;
    }
  }

  public get isBlockStackDisabled(): boolean {
    return this._calendarService.nextShows.some(
      (x) => x instanceof GraphicsShow,
    );
  }

  public async toggleBlockStack(isBlockStack: boolean) {
    this.isBlockStack = isBlockStack;
    this._block.isBlockStack = isBlockStack;
    await this._blockRepository.update(this._block);
    this.blockUpdated.next();
  }
  public handleContentEditablePaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    if (text) {
      this.name = this.name + text.replace(/[\r\n]+/g, " ");
    }
  }

  public static calculateShowsDuration(
    shows: { duration: moment.Duration }[],
  ): moment.Duration {
    let result = moment.duration(0, "hours");

    if (shows.length > 0) {
      result = shows.map((s) => s.duration).reduce((a, b) => a.add(b));
    }

    return result;
  }
  public static getRelevantBlockShows(shows: Show[]): Show[] {
    let result: Show[] = [];

    shows.sort((a, b) => (b.startTime > a.startTime ? -1 : 1));
    const startTime = moment().startOf("day");
    const endOfDay = moment().startOf("day").add(24, "hours");
    for (let i = 0; i < shows.length; i++) {
      const currentShow = shows[i];
      result.push(currentShow);
      if (startTime.add(currentShow.duration) > endOfDay) {
        break;
      }
    }
    result = result.filter(
      (s) => !(s instanceof MediaShow) || (<MediaShow>s).isContract === false,
    );
    return result;
  }

  public onDestroy() {
    this._calendarService.clear48HoursCacheOnShowChanged = true;
    this.showsChangedSubscription?.unsubscribe();
    this._calendarListComponent?.ngOnDestroy();
  }

  public async saveBlock() {
    await this._blockRepository.update(this);
  }

  public removeBlock() {
    this._ui.notify(this._ui.EVENTS.ShowConfirm, {
      title: "Delete block " + this.name,
      message: "Are you sure about that?",
      buttons: [
        {
          title: "Yes",
          callback: this._onRemoveBlockApproved.bind(this),
        },
        {
          title: "No",
          callback: () => {},
        },
      ],
    });
  }
  private async _onRemoveBlockApproved() {
    await this._blockRepository.delete(this.id);
    await this._router.navigate(["/scheduler/calendar"]);
  }

  public async duplicateBlock() {
    const fromDate = moment().utc().subtract(100, "years");
    const toDate = moment().utc().add(100, "years");

    this._ngxSmartModalService.setModalData(
      {
        sourceChannelId: this.id,
        fromTime: fromDate,
        toTime: toDate,
        isStack: this.isBlockStack,
      },
      "createBlockModal",
      true,
    );
    this._ngxSmartModalService.getModal("createBlockModal").open();
  }

  private async _wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  private async onShowsChangeAction() {
    console.log(
      "EditBlockViewModel->onShowsChange subscription executed",
      this._calendarListComponent,
    );

    if (this._duringArrangeShows) {
      console.log(
        "EditBlockViewModel->onShowsChange->_duringArrangeShows = true; canceling...",
      );
      return;
    }

    try {
      this._duringArrangeShows = true;
      console.log("EditBlockViewModel->onShowsChange->start arranging shows");
      //await this._wait(300);
      await this._showsRepository.arrangeShows(
        this.id,
        moment().startOf("day"),
        moment().startOf("day").add(24, "hours"),
        false,
      );
      await this._wait(300);
      const foundShows = await this._showsRepository.getByDates(
        this.id,
        moment().utc().subtract(100, "years"),
        moment().utc().add(100, "years"),
        false,
        null,
        false,
      );

      const relevantShows =
        EditBlockViewModel.getRelevantBlockShows(foundShows);
      const showsCount = relevantShows.length;
      let showsDuration =
        EditBlockViewModel.calculateShowsDuration(relevantShows);
      this.duration = this._block.duration = showsDuration;
      this.showsCount = this._block.showsCount = showsCount;

      this._blockRepository.updated.next(this._block);

      await this._calendarListComponent?.refreshView();
    } finally {
      this._changeDetectorRef.markForCheck();
      this._changeDetectorRef.detectChanges();
      this._duringArrangeShows = false;
    }
  }
}
